import type { VideoLocale } from '@/server/domain/santa-video/locale'
import { BOOK_MIN_PERSONAL_DETAILS, minInterestsFor } from '@/server/domain/santa-video/book-order-form'
import { MIN_PERSONAL_DETAILS } from '@/server/domain/santa-video/order-form'
import type { VideoStoryId } from '@/server/domain/santa-video/stories'

export interface OrderFormValues {
  name: string
  personalDetails: string[]
  authorizedGifts: string[]
  requestedGifts: string[]
  interests: string[]
  /** Tal como está en el input: `''` = no lo sabemos. */
  age: string
  city: string
  parentMessage: string
  email: string
  /** El reto del libro (`BookOrderForm.challenge`); la carta no lo usa. `''` = sin reto. */
  challenge: string
}

/** El formulario vacío de la carta: los hechos nacen con el mínimo de huecos del contrato. */
export const EMPTY_ORDER_VALUES: OrderFormValues = {
  name: '', personalDetails: Array(MIN_PERSONAL_DETAILS).fill(''), authorizedGifts: [''],
  requestedGifts: [''], interests: [''], age: '', city: '', parentMessage: '', email: '', challenge: '',
}

const blanks = (count: number): string[] => Array<string>(count).fill('')

/**
 * El del libro: los mínimos de su contrato (`BOOK_MIN_PERSONAL_DETAILS` y los
 * intereses que pide sin reto) y ninguna lista de regalos: el niño aún no ha
 * escrito su carta.
 */
const EMPTY_BOOK_ORDER_VALUES: OrderFormValues = {
  ...EMPTY_ORDER_VALUES,
  personalDetails: blanks(BOOK_MIN_PERSONAL_DETAILS), interests: blanks(minInterestsFor(undefined)),
  authorizedGifts: [], requestedGifts: [],
}

const EMPTY_VALUES: Record<VideoStoryId, OrderFormValues> = { letter: EMPTY_ORDER_VALUES, book: EMPTY_BOOK_ORDER_VALUES }

export function emptyOrderValuesFor(story: VideoStoryId): OrderFormValues {
  return EMPTY_VALUES[story]
}

const filled =(entries: readonly string[]) => entries.map((entry) => entry.trim()).filter(Boolean)

/** Lo común a las dos historias, sin listas de regalos ni reto. */
function commonFields(values: OrderFormValues) {
  const age = values.age.trim()
  const city = values.city.trim()
  const parentMessage = values.parentMessage.trim()
  return {
    ...(age ? { age: Number(age) } : {}),
    ...(city ? { location: { city } } : {}),
    ...(parentMessage ? { parentMessage } : {}),
  }
}

/** Las claves van en el orden de siempre: el cuerpo de la carta no cambia con la llegada del libro. */
function letterForm(values: OrderFormValues): Record<string, unknown> {
  return {
    name: values.name.trim(),
    personalDetails: filled(values.personalDetails),
    authorizedGifts: filled(values.authorizedGifts),
    requestedGifts: filled(values.requestedGifts),
    interests: filled(values.interests),
    ...commonFields(values),
  }
}

function bookForm(values: OrderFormValues): Record<string, unknown> {
  const challenge = values.challenge.trim()
  return {
    name: values.name.trim(),
    personalDetails: filled(values.personalDetails),
    interests: filled(values.interests),
    ...(challenge ? { challenge } : {}),
    ...commonFields(values),
  }
}

/**
 * Del estado del formulario al cuerpo de `POST /api/santa-video/orders`. La
 * validación de verdad es la del servidor (el contrato de la historia): esto
 * sólo quita lo vacío y no inventa nada. `tone` y `language` no viajan: los
 * pone el servidor (default y derivado del locale). La carta no manda `story`
 * (sin ella el servidor entiende carta): su cuerpo es el de siempre.
 */
const FORM_OF: Record<VideoStoryId, (values: OrderFormValues) => Record<string, unknown>> = {
  letter: letterForm,
  book: bookForm,
}

/** El `form` que se envía para esa historia, ya sin lo vacío. */
export function orderFormOf(values: OrderFormValues, story: VideoStoryId): Record<string, unknown> {
  return FORM_OF[story](values)
}

export function buildOrderRequestBody(values: OrderFormValues, locale: VideoLocale, story: VideoStoryId) {
  const email = values.email.trim()
  const form = orderFormOf(values, story)
  return story === 'letter' ? { email, locale, form } : { email, locale, story, form }
}
